/**
*  @license
* Copyright 2021, JsData. All rights reserved.
*
* This source code is licensed under the MIT license found in the
* LICENSE file in the root directory of this source tree.

* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
* ==========================================================================
*/
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { convertToNumericTensor2D } from '../utils';
import { isScikit2D, assert, isDataFrameInterface } from '../typesUtils';
import { tensorMean, tensorStd, turnZerosToOnes } from '../math';
import { TransformerMixin } from '../mixins';
import { getBackend } from '../tf-singleton';
/**
 * Standardize features by removing the mean and scaling to unit variance.
 * The standard score of a sample x is calculated as: $z = (x - u) / s$,
 * where $u$ is the mean of the training samples, and $s$ is the standard deviation of the training samples.
 *
 * @example
 * ```js
 * import { StandardScaler } from 'scikitjs'
 *
 * const data = [
      [0, 0],
      [0, 0],
      [1, 1],
      [1, 1]
    ]

    const scaler = new StandardScaler()
    const expected = scaler.fitTransform(data)
    // const expected = [
    //  [-1, -1],
    //  [-1, -1],
    //  [1, 1],
    //  [1, 1]
    // ]
 * ```
 */
var StandardScaler = /** @class */ (function (_super) {
    __extends(StandardScaler, _super);
    function StandardScaler(_a) {
        var _b = _a === void 0 ? {} : _a, _c = _b.withMean, withMean = _c === void 0 ? true : _c, _d = _b.withStd, withStd = _d === void 0 ? true : _d;
        var _this = _super.call(this) || this;
        /** Useful for pipelines and column transformers to have a default name for transforms */
        _this.name = 'StandardScaler';
        _this.tf = getBackend();
        _this.withMean = withMean;
        _this.withStd = withStd;
        _this.scale = _this.tf.tensor1d([]);
        _this.mean = _this.tf.tensor1d([]);
        _this.nFeaturesIn = 0;
        _this.nSamplesSeen = 0;
        _this.featureNamesIn = [];
        return _this;
    }
    /**
     * Fit a StandardScaler to the data.
     */
    StandardScaler.prototype.fit = function (X) {
        assert(isScikit2D(X), 'Data can not be converted to a 2D matrix.');
        var tensorArray = convertToNumericTensor2D(X);
        if (this.withMean) {
            this.mean = tensorMean(tensorArray, 0, true);
        }
        if (this.withStd) {
            var std = tensorStd(tensorArray, 0, true);
            // Deal with zero variance issues
            this.scale = turnZerosToOnes(std);
        }
        this.nSamplesSeen = tensorArray.shape[0];
        this.nFeaturesIn = tensorArray.shape[1];
        if (isDataFrameInterface(X)) {
            this.featureNamesIn = __spreadArray([], __read(X.columns), false);
        }
        return this;
    };
    /**
     * Transform the data using the fitted scaler
     */
    StandardScaler.prototype.transform = function (X) {
        assert(isScikit2D(X), 'Data can not be converted to a 2D matrix.');
        var tensorArray = convertToNumericTensor2D(X);
        if (this.withMean) {
            tensorArray = tensorArray.sub(this.mean);
        }
        if (this.withStd) {
            tensorArray = tensorArray.div(this.scale);
        }
        return tensorArray;
    };
    /**
     * Inverse transform the data using the fitted scaler
     */
    StandardScaler.prototype.inverseTransform = function (X) {
        assert(isScikit2D(X), 'Data can not be converted to a 2D matrix.');
        var tensorArray = convertToNumericTensor2D(X);
        if (this.withStd) {
            tensorArray = tensorArray.mul(this.scale);
        }
        if (this.withMean) {
            tensorArray = tensorArray.add(this.mean);
        }
        return tensorArray;
    };
    return StandardScaler;
}(TransformerMixin));
export { StandardScaler };
